import React from 'react';
import './index.css';
import './utils/swalTheme';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { Helmet } from 'react-helmet-async';

import { useAuth } from './contexts/AuthContext';
import { CustomThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import { SystemLockOverlay } from './components/common';
import Layout from './layout/Layout';

// Páginas de autenticação
import LoginPage from './pages/auth/LoginPage';
import PublicInvoicePage from './pages/PublicInvoicePage';

// Páginas principais
import DashboardSelector from './components/DashboardSelector';
import ClientsPage from './pages/ClientsPage';
import ClientDetailsPage from './pages/ClientDetailsPage';
import PreCadastrosPage from './pages/PreCadastrosPage';

import ContractDetailsPage from './pages/ContractDetailsPage';
import ContractsPage from './pages/ContractsPage';
import ServicesPage from './pages/ServicesPage';
import PlansPage from './pages/PlansPage';
import DespesasPage from './pages/DespesasPage';
import ComissoesPage from './pages/ComissoesPage';
import ChamadosPage from './pages/ChamadosPage';
import ProfilePage from './pages/ProfilePage';
import UsersPage from './pages/UsersPage';
import GrupoUsuarioPage from './pages/GrupoUsuarioPage';
import LogsPage from './pages/LogsPage';
import TemplatesPage from './pages/TemplatesPage';
import TextEditorPage from './pages/TextEditorPage';
import GatewaysPage from './pages/GatewaysPage';
import GatewaySMSPage from './pages/GatewaySMSPage';
import GatewayWhatsappPage from './pages/GatewayWhatsappPage';
import EquipmentsPage from './pages/EquipmentsPage';
import FtthPage from './pages/FtthPage';
import FtthCaixaDetailsPage from './pages/FtthCaixaDetailsPage';
import OLTDashboard from './pages/ftth/OLTDashboard';
import HolidaysPage from './pages/FeriadosPage';
import ParametrosPage from './pages/ParametrosPage';
import NasPage from './pages/NasPage';
import TesteAbasPage from './pages/TesteAbasPage';
import SorteadorPage from './pages/SorteadorPage';
import ImportDataPage from './pages/ImportDataPage';
import CreditosPage from './pages/CreditosPage';
import RelatoriosPage from './pages/RelatoriosPage';
import { RELATORIOS_PAGE_PERMISSIONS } from './pages/relatorios';

// Páginas do Caixa Financeiro
import CaixaFinanceiroPage from './pages/CaixaFinanceiroPage';
import CaixaFinanceiroDetailsPage from './pages/CaixaFinanceiroDetailsPage';


// Páginas de erro
import NotFoundPage from './pages/NotFoundPage';
import FuncionariosPage from './pages/FuncionariosPage';
import FornecedoresPage from './pages/FornecedoresPage';
import EscritoriosPage from './pages/EscritoriosPage';
import GatewaysNfcomPage from './pages/GatewaysNfcomPage';
import NotasNfcomPage from './pages/financeiro/NotasNfcomPage';
import NfcomGerarLotePage from './pages/financeiro/NfcomGerarLotePage';
import BackupManagementPage from './pages/BackupManagementPage';
import AtualizacaoPage from './pages/AtualizacaoPage';
import CgnatPage from './pages/CgnatPage';
import MensagensPage from './pages/MensagensPage';
import FaturasCobrancasPage from './pages/financeiro/FaturasCobrancasPage';
import PropostasPage from './pages/financeiro/PropostasPage';
import TarefasPage from './pages/TarefasPage';
import MinhaLicencaPage from './pages/configuracoes/MinhaLicencaPage';

// Páginas de Atendimento
import AtendimentoDashboardPage from './pages/atendimento/DashboardPage';
import RankingPage from './pages/atendimento/RankingPage';
import AtendimentosPage from './pages/atendimento/AtendimentosPage';
import ContatosAtendimentoPage from './pages/atendimento/ContatosAtendimentoPage';
import MensagensRapidasPage from './pages/atendimento/MensagensRapidasPage';
import TagsPage from './pages/atendimento/TagsPage';
import FigurinhasPage from './pages/atendimento/FigurinhasPage';
import { FlowBuilder } from './pages/atendimento/FlowBuilder';
import { FlowEditor } from './pages/atendimento/FlowBuilder/FlowEditor';

// Páginas de Monitoramento
import MonitoramentoClientesPage from './pages/monitoramento/MonitoramentoClientesPage';
import MonitoramentoCtosPage from './pages/monitoramento/MonitoramentoCtosPage';
import MonitoramentoTecnicosPage from './pages/monitoramento/MonitoramentoTecnicosPage';
import MonitoramentoTrafegoPage from './pages/monitoramento/MonitoramentoTrafegoPage';
import MonitoramentoRedePage from './pages/monitoramento/MonitoramentoRedePage';

// Páginas de Frota
import VeiculosPage from './pages/frota/VeiculosPage';
import AbastecimentosPage from './pages/frota/AbastecimentosPage';
import ManutencoesPage from './pages/frota/ManutencoesPage';
import TrocasOleoPage from './pages/frota/TrocasOleoPage';
import { LicenseModal } from './components/LicenseModal';
import { apiRequest } from './services/api';

const AppContent: React.FC = () => {
  const { user, lockedUser, loading, isLocked, unlockSystem, logout } = useAuth();
  const [licenseBlocked, setLicenseBlocked] = React.useState(false);
  const [licenseError, setLicenseError] = React.useState('');

  React.useEffect(() => {
    // Escuta evento emitido pelo interceptor de API
    const handleLicenseBlocked = (e: any) => {
      setLicenseBlocked(true);
      setLicenseError(e.detail?.message || 'Licença do sistema suspensa ou não configurada.');
    };

    window.addEventListener('sysup_license_blocked', handleLicenseBlocked);

    // Se o usuário está logado, verificar status da licença proativamente
    if (user) {
      apiRequest.get('/license/status').then((res) => {
        if (res.data && !res.data.valid) {
          setLicenseBlocked(true);
          setLicenseError(res.data.error || 'A licença do SYSUP não foi localizada ou está expirada.');
        }
      }).catch(() => {});
    }

    return () => {
      window.removeEventListener('sysup_license_blocked', handleLicenseBlocked);
    };
  }, [user]);

  // Exibe loading enquanto verifica autenticação
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (isLocked) {
    return (
      <>
        <Helmet>
          <title>SysUp - Sistema bloqueado</title>
        </Helmet>
        <SystemLockOverlay
          open={isLocked}
          lockedUser={lockedUser}
          onUnlock={unlockSystem}
          onLogout={logout}
        />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>SysUp - Sistema de Gerenciamento ISP</title>
        <meta name="description" content="Sistema completo para gerenciamento de provedores de internet" />
      </Helmet>

      <Routes>
        {/* Rotas públicas */}
        <Route
          path="/login"
          element={
            user ? <Navigate to="/dashboard" replace /> : <LoginPage />
          }
        />
        <Route path="/p/:token" element={<PublicInvoicePage />} />

        {/* Rotas protegidas */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard - acessível para todos os usuários logados */}
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardSelector />} />

          {/* Clientes - acessível para admin, financeiro e suporte */}
          <Route
            path="clients"
            element={
              <ProtectedRoute permission="clientes.acessar">
                <ClientsPage />
              </ProtectedRoute>
            }
          />

          {/* Detalhes do Cliente */}
          <Route
            path="clients/:id"
            element={
              <ProtectedRoute permission="clientes.acessar">
                <ClientDetailsPage />
              </ProtectedRoute>
            }
          />

          {/* Pré-Cadastros */}
          <Route
            path="clients/pre-cadastros"
            element={
              <ProtectedRoute permission="clientes.acessar">
                <PreCadastrosPage />
              </ProtectedRoute>
            }
          />

          {/* Lista de Contratos - acessível para admin, financeiro e suporte */}
          <Route
            path="contracts"
            element={
              <ProtectedRoute permission="contratos.acessar">
                <ContractsPage />
              </ProtectedRoute>
            }
          />

          {/* Detalhes do Contrato - acessível para admin, financeiro e suporte */}
          <Route
            path="contracts/:id"
            element={
              <ProtectedRoute permission="contratos.acessar">
                <ContractDetailsPage />
              </ProtectedRoute>
            }
          />

          {/* Lista de Serviços - acessível para admin, financeiro e suporte */}
          <Route
            path="services"
            element={
              <ProtectedRoute permission="contratos.acessar">
                <ServicesPage />
              </ProtectedRoute>
            }
          />

          {/* Planos - acessível para admin e financeiro */}
          <Route
            path="plans"
            element={
              <ProtectedRoute permission="financeiro.acessar">
                <PlansPage />
              </ProtectedRoute>
            }
          />

          {/* Faturas e Cobranças - acessível para admin e financeiro */}
          <Route
            path="faturas-cobrancas"
            element={
              <ProtectedRoute permission="financeiro.acessar">
                <FaturasCobrancasPage />
              </ProtectedRoute>
            }
          />

          {/* Propostas - acessível para admin e financeiro */}
          <Route
            path="propostas"
            element={
              <ProtectedRoute permission="administracao.propostas.acessar">
                <PropostasPage />
              </ProtectedRoute>
            }
          />


          {/* Comissões - acessível para admin e financeiro */}
          <Route
            path="comissoes"
            element={
              <ProtectedRoute permission="financeiro.comissoes.acessar">
                <ComissoesPage />
              </ProtectedRoute>
            }
          />

          {/* Despesas - acessível para admin e financeiro */}
          <Route
            path="contas-a-pagar"
            element={
              <ProtectedRoute permission="financeiro.contas_pagar.acessar">
                <DespesasPage />
              </ProtectedRoute>
            }
          />

          {/* Créditos - acessível para admin e financeiro */}
          <Route
            path="creditos"
            element={
              <ProtectedRoute permission="financeiro.creditos.acessar">
                <CreditosPage />
              </ProtectedRoute>
            }
          />

          {/* Caixa Financeiro - acessível para admin e financeiro */}
          <Route
            path="caixa-financeiro"
            element={
              <ProtectedRoute permission="financeiro.caixa.acessar">
                <CaixaFinanceiroPage />
              </ProtectedRoute>
            }
          />

          {/* Detalhes do Caixa Financeiro - acessível para admin e financeiro */}
          <Route
            path="caixa-financeiro/:id"
            element={
              <ProtectedRoute permission="financeiro.caixa.acessar">
                <CaixaFinanceiroDetailsPage />
              </ProtectedRoute>
            }
          />

          {/* Gateways WhatsApp - acessível para administradores e financeiro */}
          <Route
            path="settings/gateways-whatsapp"
            element={
              <ProtectedRoute roles={['administrador', 'financeiro']}>
                <GatewayWhatsappPage />
              </ProtectedRoute>
            }
          />

          {/* Chamados - acessível para todos */}
          <Route path="chamados" element={<ChamadosPage />} />

          {/* Atendimento WhatsApp */}
          <Route
            path="atendimento/dashboard"
            element={
              <ProtectedRoute permission="atendimento.dashboard.acessar">
                <AtendimentoDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="atendimento/ranking"
            element={
              <ProtectedRoute permission="atendimento.ranking.acessar">
                <RankingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="atendimento/atendimentos"
            element={
              <ProtectedRoute permission="atendimento.tickets.acessar">
                <AtendimentosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="atendimento/contatos"
            element={
              <ProtectedRoute permission="atendimento.contatos.acessar">
                <ContatosAtendimentoPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="atendimento/mensagens-rapidas"
            element={
              <ProtectedRoute permission="atendimento.mensagens_rapidas.acessar">
                <MensagensRapidasPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="atendimento/figurinhas"
            element={
              <ProtectedRoute permission="atendimento.figurinhas.acessar">
                <FigurinhasPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="atendimento/tags"
            element={
              <ProtectedRoute permission="atendimento.tags.acessar">
                <TagsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="atendimento/fluxo-bot"
            element={
              <ProtectedRoute roles={['administrador', 'suporte']}>
                <FlowBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="atendimento/fluxo-bot/:flowId"
            element={
              <ProtectedRoute roles={['administrador', 'suporte']}>
                <FlowEditor />
              </ProtectedRoute>
            }
          />
          {/* Tarefas - acessível para admin, financeiro e suporte */}
          <Route
            path="tarefas"
            element={
              <ProtectedRoute roles={['administrador', 'financeiro', 'suporte']}>
                <TarefasPage />
              </ProtectedRoute>
            }
          />



          {/* Equipamentos - acessível para admin, financeiro e suporte */}
          <Route
            path="equipments"
            element={
              <ProtectedRoute roles={['administrador', 'financeiro', 'suporte']}>
                <EquipmentsPage />
              </ProtectedRoute>
            }
          />

          {/* Dashboard FTTH - acessível para admin, financeiro e suporte */}
          <Route
            path="ftth"
            element={
              <ProtectedRoute roles={['administrador', 'financeiro', 'suporte']}>
                <FtthPage />
              </ProtectedRoute>
            }
          />

          {/* Detalhes da CAIXA - acessível para admin, financeiro e suporte */}
          <Route
            path="ftth/caixas/:id"
            element={
              <ProtectedRoute roles={['administrador', 'financeiro', 'suporte']}>
                <FtthCaixaDetailsPage />
              </ProtectedRoute>
            }
          />

          {/* Dashboard da OLT - acessível para admin, financeiro e suporte */}
          <Route
            path="ftth/olts/:id"
            element={
              <ProtectedRoute roles={['administrador', 'financeiro', 'suporte']}>
                <OLTDashboard />
              </ProtectedRoute>
            }
          />

          {/* Editor de Texto - acessível para todos */}
          <Route path="text-editor" element={<TextEditorPage />} />

          {/* Teste Abas - acessível para todos */}
          <Route path="teste-abas" element={<TesteAbasPage />} />

          {/* Sorteador - acessível para administradores e financeiro */}
          <Route
            path="sorteador"
            element={
              <ProtectedRoute roles={['administrador', 'financeiro']}>
                <SorteadorPage />
              </ProtectedRoute>
            }
          />

          {/* Relatórios */}
          <Route
            path="reports"
            element={
              <ProtectedRoute permission={RELATORIOS_PAGE_PERMISSIONS}>
                <RelatoriosPage />
              </ProtectedRoute>
            }
          />

          {/* Perfil - acessível para todos */}
          <Route path="profile" element={<ProfilePage />} />

          {/* Configurações - acessível apenas para administradores */}
          <Route
            path="settings/users"
            element={
              <ProtectedRoute permission="usuarios.acessar">
                <UsersPage />
              </ProtectedRoute>
            }
          />

          {/* Grupos de Usuários - acessível apenas para administradores */}
          <Route
            path="settings/grupos"
            element={
              <ProtectedRoute permission="grupos_usuarios.acessar">
                <GrupoUsuarioPage />
              </ProtectedRoute>
            }
          />

          {/* Templates - acessível apenas para administradores */}
          <Route
            path="settings/templates"
            element={
              <ProtectedRoute permission="configuracoes.acessar">
                <TemplatesPage />
              </ProtectedRoute>
            }
          />

          {/* Logs - acessível apenas para administradores */}
          <Route
            path="settings/logs"
            element={
              <ProtectedRoute permission="configuracoes.acessar">
                <LogsPage />
              </ProtectedRoute>
            }
          />

          {/* Feriados - acessível apenas para administradores */}
          <Route
            path="settings/holidays"
            element={
              <ProtectedRoute permission="configuracoes.acessar">
                <HolidaysPage />
              </ProtectedRoute>
            }
          />

          {/* Parâmetros - acessível apenas para administradores */}
          <Route
            path="settings/parameters"
            element={
              <ProtectedRoute roles={['administrador']}>
                <ParametrosPage />
              </ProtectedRoute>
            }
          />



          {/* Gateways - acessível para administradores e financeiro */}
          <Route
            path="settings/gateways"
            element={
              <ProtectedRoute roles={['administrador', 'financeiro']}>
                <GatewaysPage />
              </ProtectedRoute>
            }
          />

          {/* Gateways SMS - acessível para administradores e financeiro */}
          <Route
            path="settings/gateways-sms"
            element={
              <ProtectedRoute roles={['administrador', 'financeiro']}>
                <GatewaySMSPage />
              </ProtectedRoute>
            }
          />

          {/* Equipamentos NAS - acessível apenas para administradores */}
          <Route
            path="settings/nas"
            element={
              <ProtectedRoute roles={['administrador']}>
                <NasPage />
              </ProtectedRoute>
            }
          />

          {/* Importar Dados - acessível apenas para administradores */}
          <Route
            path="settings/import-data"
            element={
              <ProtectedRoute roles={['administrador']}>
                <ImportDataPage />
              </ProtectedRoute>
            }
          />

          {/* Backup - acessível apenas para administradores */}
          <Route
            path="settings/backup"
            element={
              <ProtectedRoute permission="configuracoes.acessar">
                <BackupManagementPage />
              </ProtectedRoute>
            }
          />

          {/* Atualização do sistema - acessível apenas para administradores */}
          <Route
            path="settings/atualizacao"
            element={
              <ProtectedRoute permission="configuracoes.atualizacao.acessar">
                <AtualizacaoPage />
              </ProtectedRoute>
            }
          />

          {/* Licença do SYSUP e Faturas - acessível para administradores */}
          <Route
            path="settings/licenca"
            element={
              <ProtectedRoute>
                <MinhaLicencaPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="licenca"
            element={
              <ProtectedRoute>
                <MinhaLicencaPage />
              </ProtectedRoute>
            }
          />

          {/* Funcionários - acessível apenas para administradores */}
          <Route
            path="settings/funcionarios"
            element={
              <ProtectedRoute roles={['administrador']}>
                <FuncionariosPage />
              </ProtectedRoute>
            }
          />

          {/* Fornecedores - acessível para administradores e financeiro */}
          <Route
            path="fornecedores"
            element={
              <ProtectedRoute permission="financeiro.fornecedores.acessar">
                <FornecedoresPage />
              </ProtectedRoute>
            }
          />

          {/* Escritórios - acessível apenas para administradores */}
          <Route
            path="escritorios"
            element={
              <ProtectedRoute roles={['administrador']}>
                <EscritoriosPage />
              </ProtectedRoute>
            }
          />

          {/* Gateways NFCom - acessível para administradores e financeiro */}
          <Route
            path="settings/gateways-nfcom"
            element={
              <ProtectedRoute roles={['administrador', 'financeiro']}>
                <GatewaysNfcomPage />
              </ProtectedRoute>
            }
          />

          {/* Notas NFCom - acessível para administradores e financeiro */}
          <Route
            path="notas-nfcom"
            element={
              <ProtectedRoute roles={['administrador', 'financeiro']}>
                <NotasNfcomPage />
              </ProtectedRoute>
            }
          />
          {/* NFCom - Gerar Lote - acessível para administradores e financeiro */}
          <Route
            path="notas-nfcom/gerar-lote"
            element={
              <ProtectedRoute roles={['administrador', 'financeiro']}>
                <NfcomGerarLotePage />
              </ProtectedRoute>
            }
          />

          {/* CGNAT - acessível para administradores e técnicos */}
          <Route
            path="cgnat"
            element={
              <ProtectedRoute permission="configuracoes.acessar">
                <CgnatPage />
              </ProtectedRoute>
            }
          />

          {/* Monitoramento */}
          <Route
            path="monitoramento/clientes"
            element={
              <ProtectedRoute roles={['administrador', 'financeiro', 'suporte', 'tecnico']}>
                <MonitoramentoClientesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="monitoramento/ctos"
            element={
              <ProtectedRoute roles={['administrador', 'financeiro', 'suporte', 'tecnico']}>
                <MonitoramentoCtosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="monitoramento/tecnicos"
            element={
              <ProtectedRoute roles={['administrador', 'financeiro', 'suporte', 'tecnico']}>
                <MonitoramentoTecnicosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="monitoramento/trafego"
            element={
              <ProtectedRoute roles={['administrador', 'financeiro', 'suporte', 'tecnico']}>
                <MonitoramentoTrafegoPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="monitoramento/rede"
            element={
              <ProtectedRoute roles={['administrador', 'financeiro', 'suporte', 'tecnico']}>
                <MonitoramentoRedePage />
              </ProtectedRoute>
            }
          />

          {/* Frota */}
          <Route
            path="frota/veiculos"
            element={
              <ProtectedRoute permission="frota.veiculos.acessar">
                <VeiculosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="frota/abastecimentos"
            element={
              <ProtectedRoute permission="frota.abastecimentos.acessar">
                <AbastecimentosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="frota/manutencoes"
            element={
              <ProtectedRoute permission="frota.manutencoes.acessar">
                <ManutencoesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="frota/trocas-oleo"
            element={
              <ProtectedRoute permission="frota.trocas_oleo.acessar">
                <TrocasOleoPage />
              </ProtectedRoute>
            }
          />

          {/* Mensagens - acessível para administradores e financeiro */}
          <Route
            path="mensagens"
            element={
              <ProtectedRoute roles={['administrador', 'financeiro']}>
                <MensagensPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Página não encontrada */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* Modal de inserção de licença quando logado */}
      <LicenseModal
        open={licenseBlocked}
        errorDetails={licenseError}
        onSuccess={() => {
          setLicenseBlocked(false);
          window.location.reload();
        }}
      />
    </>
  );
};

const App: React.FC = () => {
  return (
    <CustomThemeProvider>
      <AppContent />
    </CustomThemeProvider>
  );
};

export default App;

